import { createSanitizer, pruneEmpty } from '../../utils/sanitizeForAI'

/**
 * What the user is looking at, assembled from two kinds of source.
 *
 * `core` is the host's answer to "who and where" — the URL and the partner/user
 * identity. One provider, always present.
 *
 * `areas` is what each screen chooses to say about itself. Only the code that
 * owns a screen knows whether it addresses a resource, what to call that
 * resource's ids, or what is filtered — so no shape is imposed. A list page
 * contributes filters and counts; a detail page contributes its record's
 * identifiers under whatever names its own API uses.
 *
 * Empty values are pruned, so a page reports only what it actually knows.
 */
export type ChatContextProvider = () => unknown

/** Tight on purpose: a prompt pays for every token, and nothing here is long. */
const CONTEXT_SANITIZE_LIMITS = {
  maxKeysPerObject: 20,
  maxArrayItems: 20,
  maxStringChars: 120,
}

/** context → areas → one area → value → nested value. */
const CONTEXT_SANITIZE_DEPTH = 5

const sanitizeContext = createSanitizer(CONTEXT_SANITIZE_LIMITS)

let provideCore: ChatContextProvider | undefined
const contributors = new Map<string, ChatContextProvider>()

/** Registered once by the host; supplies the always-present `core` block. */
export const setChatCoreContextProvider = (
  provider: ChatContextProvider
): void => {
  provideCore = provider
}

/**
 * @deprecated Use `setChatCoreContextProvider`. Kept so a host pinned to an
 * older release keeps compiling through the rename.
 */
export const setChatRequestContextProvider = setChatCoreContextProvider

/**
 * Add this screen's own context under `key`, and get back the function that
 * removes it.
 *
 * Callers must remove it when the screen goes away — a contributor left behind
 * makes the agent confidently wrong about a page the user has left, which is
 * worse than a missing field. Prefer the `useChatContext` hook, which handles
 * that via effect cleanup.
 */
export const registerChatContext = (
  key: string,
  provider: ChatContextProvider
): (() => void) => {
  if (process.env.NODE_ENV !== 'production' && contributors.has(key)) {
    console.warn(
      `[labraChat] chat context key "${key}" is already registered; the newer one wins.`
    )
  }
  contributors.set(key, provider)
  return () => {
    // Identity guard: a late cleanup must not delete a newer registration that
    // has since claimed the same key.
    if (contributors.get(key) === provider) contributors.delete(key)
  }
}

/** Test/host escape hatch — drops every contributor and the core provider. */
export const resetChatContext = (): void => {
  provideCore = undefined
  contributors.clear()
}

/**
 * A contributor is page code, so it can throw. Losing its slice is acceptable;
 * losing the user's message is not.
 */
const readSlice = (provider: ChatContextProvider, key: string): unknown => {
  try {
    return pruneEmpty(sanitizeContext(provider(), CONTEXT_SANITIZE_DEPTH))
  } catch (error) {
    console.error(`[labraChat] chat context "${key}" failed`, error)
    return undefined
  }
}

/**
 * Null when nothing is known — no core provider, no contributors, or everything
 * pruned away. Read at send time so it reflects the page the user is on now.
 */
export const getChatRequestContext = (): unknown => {
  const areas: Record<string, unknown> = {}
  for (const [key, provider] of contributors) {
    const slice = readSlice(provider, key)
    if (slice !== undefined) areas[key] = slice
  }

  return (
    pruneEmpty({
      core: provideCore ? readSlice(provideCore, 'core') : undefined,
      areas,
    }) ?? null
  )
}
